import { useEffect } from "react";
import { Box } from "@chakra-ui/react";
import Badge from "../../../components/Badge";
import Button from "../../../components/Button";
import useDeleteSchemaColumn from "../../../api/useDeleteSchemaColumn";
import { sizes } from "../../../settings/theme";
import { typedSxMap } from "../../../utils/typedSxMap";
import { PiTrash } from "react-icons/pi";

type SchemaDeleteProps = {
  column: any;
  onSuccess?: () => void;
  context: any;
};

const styles = typedSxMap({
  top: {
    display: "flex",
    gap: "var(--m-s)",
    textAlign: "left",
    alignItems: "flex-start",
    paddingBottom: "var(--m-s)",
  },
  icon: {
    width: "var(--m-xxl)",
    height: "var(--m-xxl)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--color-background-error-soft)",
    borderRadius: "var(--border-radius-r)",
    flex: "0 0 var(--m-xxl)",
    border: "5px solid var(--color-background-menu-hover)",

    svg: {
      display: "block",
    },
  },

  texts: {
    flexGrow: 1,
  },

  h3: {
    fontSize: "var(--font-size-xl)",
    fontWeight: 500,
    margin: "var(--m-xs) 0 var(--m-s)",
  },
  container: {
    "& .actions": {
      gap: "var(--m)",
      display: "flex",
      justifyContent: "flex-end",
      marginTop: 0,
    },
  },
});

export default function SchemaColumnDelete({ column, onSuccess = () => null, context }: SchemaDeleteProps) {
  const { mutate, isLoading, isSuccess } = useDeleteSchemaColumn(context?.schemaId);

  const onDeleteConfirm = () => {
    mutate(column?.id);
    onSuccess();
  };

  useEffect(() => {
    if (isSuccess && onSuccess) onSuccess();
  }, [isSuccess]);

  const isTableColumn = column?.data_type === "table";

  return (
    <Box sx={styles.container}>
      <Box sx={styles.top}>
        <Box sx={styles.icon}>
          <PiTrash color="red" size={sizes.icon.large} />
        </Box>
        <Box sx={styles.texts}>
          <h2 style={{ paddingBottom: "var(--m-s)" }}>Delete Column</h2>
          <p>
            Are you sure you want to delete the column <Badge variants={["deep"]}>{column?.name}</Badge>? This action cannot be undone.
          </p>
          {isTableColumn && (
            <p style={{ color: "var(--color-text-error)" }}>
              Warning: This is a table column. Deleting it will also delete all its associated columns.
            </p>
          )}
        </Box>
      </Box>

      <fieldset disabled={isLoading} className="actions">
        <Button type="button" onClick={onSuccess}>
          Cancel
        </Button>
        <Button variants={["warning"]} type="button" onClick={onDeleteConfirm}>
          Delete
        </Button>
      </fieldset>
    </Box>
  );
}
